// Updated TeamStatsMenu.jsx to use new dashboard system
import React, { useState } from 'react';
import { BarChart2, ChevronDown, MessageCircle, Activity } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { getBrazilianStatus } from '../../utils/detectBrazil';
import { teamStatsMenuPtBR } from '../mappings/teamStatsMenu.pt-br';

/**
 * TeamStatsMenu - Sports statistics navigator
 * Now navigates to new dashboard system instead of using modals
 */
const TeamStatsMenu = ({ onSuggestionClick, DesktopTeamStats, isMobile = false }) => {
  // Navigation hook
  const navigate = useNavigate();
  
  // Check if user is Brazilian
  const isBrazilian = getBrazilianStatus();
  
  // Translation function
  const t = (text) => {
    if (!isBrazilian) return text;
    return teamStatsMenuPtBR[text] || text;
  };

  // Core state
  const [isOpen, setIsOpen] = useState(false);
  const [selectedSport, setSelectedSport] = useState(null);
  const [sportMessage, setSportMessage] = useState(null);

  // Sport data with correct navigation paths - Live/Pre-season first, then offseason/coming soon
  const SPORTS = [
    // Row 1: All live and pre-season sports
    { 
      id: 'Soccer', 
      name: 'Football/Soccer', 
      icon: '⚽',
      status: 'live',
      path: '/football',
      gradient: 'from-teal-500/20 to-emerald-500/20',
      hoverGradient: 'from-teal-500/30 to-emerald-500/30'
    },
    { 
      id: 'MLB', 
      name: 'MLB', 
      icon: '⚾',
      status: 'live',
      path: '/mlb',
      gradient: 'from-red-500/20 to-blue-500/20',
      hoverGradient: 'from-red-500/30 to-blue-500/30'
    },
    { 
      id: 'MMA', 
      name: 'MMA/UFC', 
      icon: '🥊',
      status: 'live',
      path: '/mma-fights',
      gradient: 'from-red-500/20 to-gray-500/20',
      hoverGradient: 'from-red-500/30 to-gray-500/30'
    },
    {
      id: 'NFL',
      name: 'NFL',
      icon: '🏈',
      status: 'live',
      path: '/nfl',
      gradient: 'from-emerald-500/20 to-blue-500/20',
      hoverGradient: 'from-emerald-500/30 to-blue-500/30'
    },
    {
      id: 'NBA',
      name: 'NBA',
      icon: '🏀',
      status: 'live',
      path: '/nba',
      gradient: 'from-orange-500/20 to-purple-500/20',
      hoverGradient: 'from-orange-500/30 to-purple-500/30'
    },
    {
      id: 'NHL',
      name: 'NHL',
      icon: '🏒',
      status: 'live',
      path: '/nhl',
      gradient: 'from-blue-500/20 to-cyan-500/20',
      hoverGradient: 'from-blue-500/30 to-cyan-500/30'
    },
    {
      id: 'NCAAF',
      name: 'NCAAF',
      icon: '🏆',
      status: 'live',
      path: '/ncaaf',
      gradient: 'from-amber-500/20 to-red-500/20',
      hoverGradient: 'from-amber-500/30 to-red-500/30'
    },
    {
      id: 'Volleyball',
      name: 'Volleyball',
      icon: '🏐',
      status: 'live',
      path: '/volleyball',
      gradient: 'from-pink-500/20 to-purple-500/20',
      hoverGradient: 'from-pink-500/30 to-purple-500/30'
    },
    // Row 2: Coming soon sports
    { 
      id: 'Tennis', 
      name: 'Tennis', 
      icon: '🎾',
      status: 'coming soon',
      message: 'Coming Soon',
      gradient: 'from-lime-500/20 to-yellow-500/20',
      hoverGradient: 'from-lime-500/30 to-yellow-500/30'
    },
    // Golf - Commented out for now, can be added back later
    // { 
    //   id: 'Golf', 
    //   name: 'Golf', 
    //   icon: '⛳',
    //   status: 'coming soon',
    //   message: 'Coming Soon',
    //   gradient: 'from-emerald-500/20 to-lime-500/20',
    //   hoverGradient: 'from-emerald-500/30 to-lime-500/30'
    // }
  ];

  // Sport selection logic - now navigates to dashboards
  const selectSport = (sportId) => {
    const sport = SPORTS.find(s => s.id === sportId);
    
    // Toggle if already selected
    if (sportId === selectedSport) {
      setSelectedSport(null);
      setSportMessage(null);
      return;
    }
    
    setSelectedSport(sportId);
    
    // Navigate to dashboard if path exists
    if (sport && sport.path) {
      setIsOpen(false);
      navigate(sport.path);
    }
    // For sports without dashboards yet, show message
    else if (sport && sport.message) {
      setSportMessage(sport.message);
    }
    else {
      setSportMessage(null);
    }
  };
  
  return (
    <>
      {/* Button trigger */}
      <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3.5 py-1.5 rounded-full transition-all text-emerald-400 hover:text-emerald-300 border border-emerald-500/20 hover:border-emerald-500/40 bg-emerald-500/10 shadow-sm"
        aria-label="Game explorer"
      >
        <BarChart2 className="w-4 h-4" />
        <span className="text-sm font-medium">{t('Game Explorer')}</span>
        <motion.div
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.2, ease: "easeInOut" }}
        >
          <ChevronDown className="w-3.5 h-3.5" />
        </motion.div>
      </button>
      </div>
      
      {/* Full-width Panel - Bloomberg Terminal Style */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.15, ease: 'easeOut' }}
            className="fixed left-0 right-0 top-[57px] z-40 bg-gradient-to-b from-slate-800 to-slate-900 backdrop-blur-xl border-b border-white/10 overflow-hidden"
            style={{ position: 'absolute', top: '57px' }}
          >

            <div className="w-full max-w-6xl mx-auto px-6 py-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <BarChart2 className="w-5 h-5 text-zinc-400" />
                  <div>
                    <h3 className="text-base font-medium text-white">
                      {t('Quick Game Explorer')}
                    </h3>
                    <p className="text-xs text-zinc-500 mt-0.5">Select a sport to view data and odds</p>
                  </div>
                </div>
                <button
                  onClick={() => setIsOpen(false)}
                  className="text-sm text-zinc-400 hover:text-white transition-colors"
                >
                  Close
                </button>
              </div>

              <div className="h-px w-full mb-6 bg-zinc-800" />

              {/* Content area */}
              <div>
                <motion.div
                  initial={{ opacity: 0, y: 5 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.15 }}
                >
                  {/* Quick Access to Sports Hub */}
                  <div className="mb-6 flex justify-start">
                    <button
                      onClick={() => {
                        setIsOpen(false);
                        navigate('/sports-hub');
                      }}
                      className="text-sm text-emerald-400 hover:text-emerald-300 transition-colors flex items-center gap-2"
                    >
                      <Activity className="w-4 h-4" />
                      {t('Open Sports Hub')}
                      <span className="text-zinc-500">→</span>
                    </button>
                  </div>
                    
                  {/* Sports Grid - Text-First Bloomberg Style */}
                  <div className="grid grid-cols-4 gap-3">
                    {SPORTS.map(sport => (
                      <button
                        key={sport.id}
                        onClick={() => selectSport(sport.id)}
                        className={`group relative px-4 py-3 flex items-center gap-3 border transition-colors duration-150 text-left ${
                          selectedSport === sport.id
                            ? 'bg-slate-800 border-emerald-500/40 text-white'
                            : 'bg-slate-900/50 border-slate-700 hover:border-slate-600 text-zinc-400 hover:text-white'
                        }`}
                      >
                        <span className="text-2xl">{sport.icon}</span>

                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium mb-0.5">{sport.name}</div>

                          {/* Status indicators - minimal */}
                          <div className="flex items-center gap-1.5 text-xs text-zinc-500">
                            {sport.status === 'live' && (
                              <>
                                <span className="w-1 h-1 bg-emerald-500 rounded-full"></span>
                                <span>Live</span>
                              </>
                            )}
                            {sport.status === 'pre-season' && (
                              <>
                                <span className="w-1 h-1 bg-amber-500 rounded-full"></span>
                                <span>Pre-Season</span>
                              </>
                            )}
                            {sport.status === 'offseason' && (
                              <span>Off-season</span>
                            )}
                            {sport.status === 'coming soon' && (
                              <span>Coming Soon</span>
                            )}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>

                  {/* Sport message */}
                  {sportMessage && (
                    <div className="mt-4 p-3 border border-zinc-800 bg-zinc-950 text-center">
                      <p className="text-sm text-zinc-400">{sportMessage}</p>
                    </div>
                  )}

                  {/* Quick tips */}
                  <div className="mt-6 text-left">
                    <div className="flex items-center gap-2 mb-2 text-zinc-500">
                      <MessageCircle className="w-4 h-4" />
                      <span className="text-xs font-medium uppercase tracking-wider">Quick Tips</span>
                    </div>
                    <p className="text-sm text-zinc-400 leading-relaxed">
                      Select a sport above to view data, stats, standings, and odds. No lengthy chats needed.
                    </p>
                  </div>
                </motion.div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default TeamStatsMenu;